// Shared DRF error router. Turns an axios error from a DRF response
// into a flat { field: message } map a widget can distribute to per-Field
// `error` props.
//
// Output shape:
//   { [field]: "message", _form?: "message" }
// where _form holds detail / non_field_errors / fallback messages.
//
// Extracted from features/companyContact/api/errors.js (Phase 9) and
// features/companyJoinRequest/api/errors.js (Phase 11) on the third
// consumer (Phase 12 — client contacts). Byte-identical to the existing
// feature-local copies; the two existing copies are deliberately NOT
// migrated to import from here (preserving their verified state, per
// migration/phase12-nested-array-plan.md §12.3).
//
// API surface is intentionally minimal — no parametrisation, no schema,
// no class. If a future consumer needs different error-routing
// semantics, copy and diverge rather than parametrise.

const THROTTLE_UA = "Забагато запитів. Спробуйте пізніше.";
const THROTTLE_EN_RE = /expected available in \d+ seconds?|request was throttled/i;

export const messageFromError = (error) => {
  if (!error) return "Помилка з'єднання.";
  if (error.code === "PROPERTY_UPLOAD_ID_MISSING" && error.message) {
    return error.message;
  }
  const status = error.response && error.response.status;
  if (status === 429) {
    return THROTTLE_UA;
  }
  if (error.response && error.response.data) {
    const data = error.response.data;
    if (typeof data === "string") {
      return THROTTLE_EN_RE.test(data) ? THROTTLE_UA : data;
    }
    if (typeof data.detail === "string") {
      return THROTTLE_EN_RE.test(data.detail) ? THROTTLE_UA : data.detail;
    }
  }
  if (error.message && typeof error.message === "string") {
    return THROTTLE_EN_RE.test(error.message) ? THROTTLE_UA : error.message;
  }
  return "Помилка з'єднання.";
};

const localizeThrottleText = (text) =>
  typeof text === "string" && THROTTLE_EN_RE.test(text) ? THROTTLE_UA : text;

const pickFirstString = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) {
    return typeof value[0] === "string" ? value[0] : String(value[0]);
  }
  return undefined;
};

export const parseDrfFieldErrors = (error, editableFields) => {
  const data = error && error.response && error.response.data;

  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return { _form: messageFromError(error) };
  }

  const next = {};

  for (const field of editableFields) {
    const fieldMessage = pickFirstString(data[field]);
    if (fieldMessage) {
      next[field] = fieldMessage;
    }
  }

  const formMessage = localizeThrottleText(pickFirstString(data.non_field_errors))
    || localizeThrottleText(
      typeof data.detail === "string" ? data.detail : undefined,
    );
  if (formMessage) {
    next._form = formMessage;
  }

  if (Object.keys(next).length === 0) {
    next._form = messageFromError(error);
  }

  return next;
};
